/**
 * Track Name Utility Functions
 * Handles track name normalization and display formatting
 * Uses centralized track reference data for consistency
 */

// Fallback track reference (for immediate use before async load)
const FALLBACK_TRACK_REFERENCE = {
  'Atlanta Motor Speedway': { fullName: 'Atlanta Motor Speedway', shortName: 'Atlanta', abbreviation: 'ATL' },
  'Daytona International Speedway': { fullName: 'Daytona International Speedway', shortName: 'Daytona', abbreviation: 'DAY' },
  'Talladega Superspeedway': { fullName: 'Talladega Superspeedway', shortName: 'Talladega', abbreviation: 'TAL' },
  'Charlotte Motor Speedway': { fullName: 'Charlotte Motor Speedway', shortName: 'Charlotte', abbreviation: 'CTL' },
  'Charlotte Motor Speedway Road Course': { fullName: 'Charlotte Motor Speedway Road Course', shortName: 'Charlotte Roval', abbreviation: 'ROV' },
  'Las Vegas Motor Speedway': { fullName: 'Las Vegas Motor Speedway', shortName: 'Las Vegas', abbreviation: 'LVS' },
  'Phoenix Raceway': { fullName: 'Phoenix Raceway', shortName: 'Phoenix', abbreviation: 'PHO' },
  'Homestead-Miami Speedway': { fullName: 'Homestead-Miami Speedway', shortName: 'Homestead', abbreviation: 'HOM' },
  'Texas Motor Speedway': { fullName: 'Texas Motor Speedway', shortName: 'Texas', abbreviation: 'TEX' },
  'Kansas Speedway': { fullName: 'Kansas Speedway', shortName: 'Kansas', abbreviation: 'KAN' },
  'Michigan International Speedway': { fullName: 'Michigan International Speedway', shortName: 'Michigan', abbreviation: 'MCH' },
  'Bristol Motor Speedway': { fullName: 'Bristol Motor Speedway', shortName: 'Bristol', abbreviation: 'BRI' },
  'Martinsville Speedway': { fullName: 'Martinsville Speedway', shortName: 'Martinsville', abbreviation: 'MAR' },
  'Richmond Raceway': { fullName: 'Richmond Raceway', shortName: 'Richmond', abbreviation: 'RCH' },
  'Dover Motor Speedway': { fullName: 'Dover Motor Speedway', shortName: 'Dover', abbreviation: 'DOV' },
  'Pocono Raceway': { fullName: 'Pocono Raceway', shortName: 'Pocono', abbreviation: 'POC' },
  'Indianapolis Motor Speedway': { fullName: 'Indianapolis Motor Speedway', shortName: 'Indianapolis', abbreviation: 'IND' },
  'Indianapolis Motor Speedway Road Course': { fullName: 'Indianapolis Motor Speedway Road Course', shortName: 'Indianapolis RC', abbreviation: 'INDG' },
  'Watkins Glen International': { fullName: 'Watkins Glen International', shortName: 'Watkins Glen', abbreviation: 'GLEN' },
  'Sonoma Raceway': { fullName: 'Sonoma Raceway', shortName: 'Sonoma', abbreviation: 'SON' },
  'Circuit of the Americas': { fullName: 'Circuit of the Americas', shortName: 'COTA', abbreviation: 'COTA' },
  'Darlington Raceway': { fullName: 'Darlington Raceway', shortName: 'Darlington', abbreviation: 'DAR' },
  'Nashville Superspeedway': { fullName: 'Nashville Superspeedway', shortName: 'Nashville', abbreviation: 'NSH' },
  'New Hampshire Motor Speedway': { fullName: 'New Hampshire Motor Speedway', shortName: 'New Hampshire', abbreviation: 'NHA' },
  'Iowa Speedway': { fullName: 'Iowa Speedway', shortName: 'Iowa', abbreviation: 'IOWA' },
  'World Wide Technology Raceway at Gateway': { fullName: 'World Wide Technology Raceway at Gateway', shortName: 'Gateway', abbreviation: 'GTW' },
  'Chicago Street Course': { fullName: 'Chicago Street Course', shortName: 'Chicago', abbreviation: 'CHI' },
  'Autodromo Hermanos Rodriguez': { fullName: 'Autodromo Hermanos Rodriguez', shortName: 'Mexico City', abbreviation: 'MEX' }
};

// Track name normalization mapping (for data consistency)
// Maps alternative/rebranded names to the canonical name
export const TRACK_NORMALIZATION_MAP = {
  'EchoPark Speedway': 'Atlanta Motor Speedway',
  'Atlanta Motor Speedway at EchoPark': 'Atlanta Motor Speedway',
  // Add more mappings here as track names change or rebrand
  'Charlotte Roval': 'Charlotte Motor Speedway Road Course',
  'Gateway': 'World Wide Technology Raceway at Gateway',
  'Indianapolis Grand Prix Circuit': 'Indianapolis Motor Speedway Road Course'
};

/**
 * Get track information from reference data
 * @param {string} trackName - The track name
 * @returns {object} - Track reference object with fullName, shortName, abbreviation
 */
const getTrackReference = (trackName) => {
  if (!trackName) return null;
  
  const trackRef = FALLBACK_TRACK_REFERENCE[trackName];
  if (trackRef) return trackRef;
  
  // If not found, return the original name in all fields
  return {
    fullName: trackName,
    shortName: trackName,
    abbreviation: trackName
  };
};

/**
 * Get full display name for a track (removes unnecessary branding)
 * @param {string} trackName - The track name
 * @returns {string} - Full display name (e.g., "Atlanta Motor Speedway")
 */
export const getDisplayTrackName = (trackName) => {
  if (!trackName) return trackName;
  const normalized = normalizeTrackName(trackName);
  const trackRef = getTrackReference(normalized);
  return trackRef ? trackRef.fullName : trackName;
};

/**
 * Get short display name for a track (for space-constrained areas)
 * @param {string} trackName - The track name
 * @returns {string} - Short display name (e.g., "Atlanta")
 */
export const getShortTrackName = (trackName) => {
  if (!trackName) return trackName;
  const normalized = normalizeTrackName(trackName);
  const trackRef = getTrackReference(normalized);
  return trackRef ? trackRef.shortName : trackName;
};

/**
 * Get track abbreviation (for very limited space)
 * @param {string} trackName - The track name
 * @returns {string} - Track abbreviation (e.g., "ATL")
 */
export const getTrackAbbreviation = (trackName) => {
  if (!trackName) return trackName;
  const normalized = normalizeTrackName(trackName);
  const trackRef = getTrackReference(normalized);
  return trackRef ? trackRef.abbreviation : trackName;
};

/**
 * Normalize track name to canonical form
 * @param {string} trackName - Track name that might need normalization
 * @returns {string} - Canonical track name
 */
export const normalizeTrackName = (trackName) => {
  if (!trackName) return trackName;
  return TRACK_NORMALIZATION_MAP[trackName] || trackName;
};

/**
 * Get the current track name from next race data
 * @param {object} nextRaceData - Next race data object
 * @returns {string} - Current track display name
 */
export const getCurrentTrackDisplayName = (nextRaceData) => {
  const fullTrackName = nextRaceData?.next_race_track;
  if (!fullTrackName) return 'Unknown Track';
  
  const normalized = normalizeTrackName(fullTrackName);
  return getDisplayTrackName(normalized);
};